import { useContext, useEffect, useState } from "react";
import { Context } from "../../../../context";
import { Box, Stack, Typography } from "@mui/material";
import { BaseTable } from "../../../../components/table";
import { Layout } from "../../../../containers/layout/dashboard";
import { CurrencyWrapper } from "./styled";
import { BaseLabel } from "../../../../components/formfields/label/styled";
import { BaseFieldSet } from "../../../../components/formfields/fieldset/styled";
import { BaseInput } from "../../../../components/formfields/input/styled";
import { BaseButton } from "../../../../components/button/styled";
import AddIcon from "@mui/icons-material/Add";
import { BaseConfirmationModal } from "../../../../components/modals/confirmation";
import { SearchIcon } from "../../../../assets";
import Cookies from "universal-cookie";
import { useNavigate } from "react-router-dom";
import { retrieveAllCurrencyService } from "../../../../utils/apis/configuration/currency/retrieveAllCurrencies";
import { deleteCurrencyService } from "../../../../utils/apis/configuration/currency/deleteCurrency";
import { retrieveCountryByIdService } from "../../../../config/country/countryById";
import { useQuery } from "@tanstack/react-query";

export const Currency = () => {
	const headers = ["Country", "Currency Name", "Currency Symbol", "Action"];

	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { setActiveNavItem } = useContext(Context);

	const [filter, setFilter] = useState<string>("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [selectedCurrencyId, setSelectedCurrencyId] = useState<string>("");
	const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

	useEffect(() => {
		setActiveNavItem("Configuration");
	}, [setActiveNavItem]);

	const { data: filteredCurrency, refetch: refetchFilteredCurrency } = useQuery(
		{
			queryKey: ["filteredCurrency", TOKEN, filter],
			queryFn: async () => {
				const currencies = await retrieveAllCurrencyService(TOKEN, filter);
				const transformedCurrencies = await Promise.all(
					currencies.map(async (currency: any) => {
						const country = await retrieveCountryByIdService(
							TOKEN,
							currency.country
						);
						return {
							...currency,
							country: country.title,
						};
					})
				);
				return transformedCurrencies;
			},
			enabled: !!TOKEN,
		}
	);

	const handleCallToActionClick = async (
		e:
			| React.MouseEvent<HTMLButtonElement, MouseEvent>
			| React.MouseEvent<HTMLSpanElement, MouseEvent>,
		id: string,
		action?: string
	) => {
		if (action === "edit") {
			return navigate(`/configuration/currency/editcurrency/${id}`);
		}
		if (action === "delete") {
			setSelectedCurrencyId(id);
			return setIsConfirmationModalOpen(true);
		}
	};

	const handleConfirmationModalPersist = () => {
		return setIsConfirmationModalOpen(true);
	};

	const handleCancelClick = () => {
		// reset error to default
		setError(null);
		return setIsConfirmationModalOpen(false);
	};

	const handleFilterChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const value = e.target.value;
		setFilter(value);
	};

	const handleNavigateToAddCurrencyForm = () => {
		return navigate(`/configuration/currency/addcurrency`);
	};

	const handleDeleteCurrency = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		id: string
	) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);
		try {
			const response = await deleteCurrencyService(id, TOKEN);
			if (response.status === "success") {
				setIsLoading(false);
				setIsConfirmationModalOpen(false);
				refetchFilteredCurrency();
			} else {
				setIsLoading(false);
				setError("Currency deletion failed. Please try again.");
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Currency deletion failed. ${error.message}`);
			console.error("Currency deletion failed:", error);
		}
	};

	return (
		<Layout id="currency">
			<CurrencyWrapper>
				<BaseConfirmationModal
					error={error}
					isLoading={isLoading}
					open={isConfirmationModalOpen}
					className="delete-currency-confirmation-modal"
					body="Once deleted, you will not be able to recover this information!"
					handleClose={handleConfirmationModalPersist}
					handleConfirm={(e) => handleDeleteCurrency(e, selectedCurrencyId)}
					handleCancel={handleCancelClick}
				/>
				<Stack
					gap={{
						mobile: "calc(var(--flex-gap)/2)",
						miniTablet: "var(--flex-gap)",
					}}
					alignItems={{ miniTablet: "center" }}
					justifyContent={"space-between"}
					direction={{ miniTablet: "row" }}
					overflow={"hidden"}
				>
					<Box>
						<Typography
							variant="h1"
							fontFamily={"Inter"}
							fontWeight={700}
							fontSize={24}
							lineHeight={"normal"}
							color={"#373737"}
						>
							Setup Currency
						</Typography>
					</Box>
					<Box>
						<BaseButton
							variant="contained"
							radius="7px"
							fontSize={16}
							fontWeight={800}
							bgcolor="#791377"
							startIcon={<AddIcon />}
							padding="calc(var(--basic-padding)/3) calc(var(--basic-padding) * 1.5)"
							sx={{
								width: { mobile: "-webkit-fill-available", miniTablet: "auto" },
							}}
							onClick={handleNavigateToAddCurrencyForm}
						>
							<Typography
								variant={"button"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"inherit"}
								textTransform={"inherit"}
							>
								Add
							</Typography>
						</BaseButton>
					</Box>
				</Stack>
				<Box
					marginBlock={"calc(var(--basic-margin)/2) calc(var(--basic-margin))"}
				>
					<form>
						<BaseFieldSet>
							<BaseLabel colour="#505050" fontWeight={500} fontSize="15px">
								Search
							</BaseLabel>
							<BaseInput
								name="filter"
								colour="#666666"
								bgcolor="#FFFFFF"
								fontWeight={400}
								value={filter}
								placeholder="Enter Currency Name"
								onChange={(e) => handleFilterChange(e)}
								startAdornment={
									<Box component={"div"} className="search-icon-box">
										<SearchIcon />
									</Box>
								}
							/>
						</BaseFieldSet>
					</form>
				</Box>
				<BaseTable
					headers={headers}
					rows={filteredCurrency as Record<any, any>[]}
					location="all-currencies"
					handleItemClick={handleCallToActionClick}
				/>
			</CurrencyWrapper>
		</Layout>
	);
};
